import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { HeartPulse, Lightbulb, Plus, Trash2, Save, Loader2, Star, X, Printer } from 'lucide-react';
import { useStore } from '../context/StoreContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPickInput = ({ pins, setPins, getAuthHeader, terms = [], pinLinks = {}, setPinLink, pinStatus = {} }) => {
  const { storeName } = useStore();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = React.useRef(null);

  const search = (value) => {
    setTerm(value);
    if (timer.current) clearTimeout(timer.current);
    if (value.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        // Letters may be split by spaces/hyphens in the product name ("Pureencapsulations" → "Pure Encapsulations")
        const rx = value.trim().split(/\s+/)
          .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').split('').join('[\\s-]?'))
          .join('.*');
        const res = await axios.get(`${API_URL}/products?search=${encodeURIComponent(rx)}&limit=40`, { headers: getAuthHeader() });
        setResults(res.data || []);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const addPin = (name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    if (!pins.some(p => p.toLowerCase() === clean.toLowerCase())) setPins([...pins, clean]);
    setTerm('');
    setResults([]);
  };

  // Enter = barcode scanner or keyboard: scanned barcodes resolve to the product, anything else pins the typed text
  const onEnter = async () => {
    const value = term.trim();
    if (!value) return;
    if (timer.current) clearTimeout(timer.current);
    if (/^\d{6,}$/.test(value)) {
      try {
        const res = await axios.get(`${API_URL}/products?search=${encodeURIComponent(value)}&limit=5`, { headers: getAuthHeader() });
        const hit = (res.data || []).find(p => p.barcode === value) || (res.data || [])[0];
        if (hit) { addPin(hit.name); toast.success(`Pinned ${hit.name}`); return; }
        toast.error('No product found for that barcode');
        setTerm('');
        return;
      } catch (_) { toast.error('Barcode lookup failed'); return; }
    }
    addPin(value);
  };

  return (
    <div className="space-y-2">
      {pins.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pins.map(p => {
            const key = p.toLowerCase();
            const linked = pinLinks[key] || pinStatus[key] || (Object.entries(pinStatus).find(([n]) => key.split(/\s+/).every(w => n.includes(w))) || [])[1] || '';
            const missing = !linked;
            return (
              <span key={p} className={`inline-flex items-center gap-1 border text-xs font-medium rounded-full pl-2.5 pr-1.5 py-1 ${missing ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-100 border-amber-300 text-amber-900'}`} data-testid={`pinned-chip-${p.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                <Star className={`w-3 h-3 ${missing ? 'text-red-400' : 'text-amber-500 fill-amber-400'}`} />
                {p}
                {terms.length > 0 && (
                  <select
                    value={pinLinks[key] || ''}
                    onChange={(e) => setPinLink(p, e.target.value)}
                    title="Which ingredient's description should print with this product?"
                    className={`ml-1 max-w-[150px] truncate rounded-full border-0 bg-white/70 px-1.5 py-0.5 text-[11px] ${missing ? 'text-red-700 font-semibold' : 'text-teal-800'}`}
                    data-testid={`pinned-chip-link-${p.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    <option value="">{missing ? '⚠ no description — pick ingredient' : `↳ ${linked} (auto)`}</option>
                    {terms.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                <button onClick={() => setPins(pins.filter(x => x !== p))} className="ml-0.5 hover:text-red-600" data-testid={`pinned-chip-remove-${p.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Input
          value={term}
          onChange={(e) => search(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
          placeholder="Type a product name or scan its barcode — pick from the list"
          data-testid="protocol-pinned-search"
        />
        {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />}
        {results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid="protocol-pinned-results">
            {results.map(p => (
              <button
                key={p.id}
                onClick={() => addPin(p.name)}
                className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b last:border-b-0"
                data-testid={`protocol-pinned-result-${p.id}`}
              >
                <p className="text-sm font-medium truncate">{p.name}</p>
                <p className="text-xs text-slate-500">
                  ${(p.sell_price || 0).toFixed(2)} · {storeName('orewa')}: {p.stock_orewa ?? 0} · {storeName('eastridge')}: {p.stock_eastridge ?? 0}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ConditionCard = ({ cond, onSaved, onDeleted, onPrint, getAuthHeader }) => {
  const [name, setName] = useState(cond.name);
  const [stack, setStack] = useState(
    (cond.stack && cond.stack.length)
      ? cond.stack
      : (cond.keywords || []).map(k => ({ term: k, benefit: '', caution: '', dose: '' }))
  );
  const [pinned, setPinned] = useState(cond.pinned || []);
  const [staffNotes, setStaffNotes] = useState(cond.staff_notes || '');
  const [pinLinks, setPinLinks] = useState(cond.pin_links || {});
  const [pinStatus, setPinStatus] = useState({});
  const [saving, setSaving] = useState(false);

  // Which ingredient each pin currently resolves to on the printed card (auto name match or owner link)
  const refreshPinStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/conditions/shelf-card?condition=${encodeURIComponent(name)}&store=orewa`, { headers: getAuthHeader() });
      const status = {};
      (res.data.stack || []).forEach(s => (s.products || []).forEach(pr => { status[pr.name.toLowerCase()] = s.term; }));
      setPinStatus(status);
    } catch (_) { /* card preview only */ }
  };
  useEffect(() => { refreshPinStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i, field, value) => setStack(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const removeRow = (i) => setStack(prev => prev.filter((_, idx) => idx !== i));
  const addRow = () => setStack(prev => [...prev, { term: '', benefit: '', caution: '', dose: '' }]);

  const save = async (pinsOverride, silent = false, linksOverride) => {
    const pins = pinsOverride ?? pinned;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/conditions`, {
        name,
        stack: stack.filter(r => (r.term || '').trim()),
        pinned: pins,
        staff_notes: staffNotes,
        pin_links: linksOverride ?? pinLinks,
      }, { headers: getAuthHeader() });
      if (!silent) toast.success(`'${name}' saved`);
      if (!silent) onSaved();
      refreshPinStatus();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Pins persist the moment they're added/removed so they can never be lost or missed by the shelf card
  const updatePins = (next) => { setPinned(next); save(next, true); };
  const setPinLink = (pin, term) => {
    const next = { ...pinLinks };
    if (term) next[pin.toLowerCase()] = term; else delete next[pin.toLowerCase()];
    setPinLinks(next);
    save(undefined, true, next);
  };

  const printCard = async () => {
    if (await save(undefined, true)) onPrint(name);
  };

  const remove = async () => {
    if (!window.confirm(`Delete the '${cond.name}' protocol?`)) return;
    try {
      await axios.delete(`${API_URL}/conditions/${encodeURIComponent(cond.name)}`, { headers: getAuthHeader() });
      toast.success(`'${cond.name}' deleted`);
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid={`protocol-card-${cond.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="font-bold max-w-xs" data-testid="protocol-name-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={printCard} disabled={saving} className="border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="protocol-print-btn">
              <Printer className="w-4 h-4 mr-1" /> Shelf Card
            </Button>
            <Button size="sm" onClick={() => save()} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="protocol-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
            <Button size="sm" variant="ghost" onClick={remove} className="text-red-500 hover:text-red-700" data-testid="protocol-delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Supplement stack (priority order — benefit, caution &amp; dose shown to staff at the till)</Label>
          <div className="space-y-1.5 mt-1">
            <div className="hidden sm:grid grid-cols-[150px_1fr_1fr_1fr_32px] gap-1.5 text-[10px] uppercase tracking-wide text-slate-400 px-1">
              <span>Supplement / ingredient</span><span>Primary benefit</span><span>Key caution</span><span>Dose &amp; timing</span><span></span>
            </div>
            {stack.map((row, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[150px_1fr_1fr_1fr_32px] gap-1.5 items-center" data-testid={`stack-row-${i}`}>
                <Input value={row.term} onChange={(e) => setRow(i, 'term', e.target.value)} placeholder="e.g. berberine" className="h-8 text-sm font-medium" data-testid={`stack-term-${i}`} />
                <Input value={row.benefit} onChange={(e) => setRow(i, 'benefit', e.target.value)} placeholder="Primary benefit" className="h-8 text-sm" data-testid={`stack-benefit-${i}`} />
                <Input value={row.caution} onChange={(e) => setRow(i, 'caution', e.target.value)} placeholder="Key caution" className="h-8 text-sm" data-testid={`stack-caution-${i}`} />
                <Input value={row.dose || ''} onChange={(e) => setRow(i, 'dose', e.target.value)} placeholder="e.g. 500mg × 3/day with meals" className="h-8 text-sm" data-testid={`stack-dose-${i}`} />
                <button onClick={() => removeRow(i)} className="text-slate-400 hover:text-red-500 justify-self-center" data-testid={`stack-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addRow} className="border-slate-200 text-slate-600 h-7 text-xs" data-testid="stack-add-row-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add supplement
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase text-amber-600 flex items-center gap-1">
            <Star className="w-3 h-3" /> Pinned products (printed on the shelf card — each needs an ingredient so staff get its description)
          </Label>
          <ProductPickInput
            pins={pinned} setPins={updatePins} getAuthHeader={getAuthHeader}
            terms={stack.map(r => (r.term || '').trim()).filter(Boolean)}
            pinLinks={pinLinks} setPinLink={setPinLink} pinStatus={pinStatus}
          />
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Staff screening notes (one question per line — printed on the shelf card)</Label>
          <Textarea
            value={staffNotes}
            onChange={(e) => setStaffNotes(e.target.value)}
            rows={4}
            placeholder={"Are you on any antidepressants or psychiatric medications?\nHave you had B12, Vitamin D, Iron & thyroid checked recently?"}
            className="mt-1 text-sm"
            data-testid="protocol-staff-notes"
          />
        </div>
      </CardContent>
    </Card>
  );
};

const NudgeRow = ({ nudge, onChanged, getAuthHeader }) => {
  const [matchText, setMatchText] = useState(nudge.match_text || '');
  const [message, setMessage] = useState(nudge.message || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/nudges`, { id: nudge.id, match_text: matchText, message }, { headers: getAuthHeader() });
      toast.success('Nudge saved');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API_URL}/nudges/${nudge.id}`, { headers: getAuthHeader() });
      toast.success('Nudge deleted');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-start border rounded-sm p-3" data-testid={`nudge-row-${nudge.id}`}>
      <Input value={matchText} onChange={(e) => setMatchText(e.target.value)} placeholder="product words e.g. turmeric|curcumin" className="sm:max-w-[220px]" data-testid="nudge-match-input" />
      <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Staff tip shown when scanned..." className="flex-1" data-testid="nudge-message-input" />
      <div className="flex gap-1 shrink-0">
        <Button size="sm" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="nudge-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={remove} className="text-red-500 hover:text-red-700" data-testid="nudge-delete-btn">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default function ProtocolEditor() {
  const { getAuthHeader } = useAuth();
  const { selectedStore, getCurrentStore } = useStore();
  const [conditions, setConditions] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [adding, setAdding] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = async () => {
    try {
      const [c, n] = await Promise.all([
        axios.get(`${API_URL}/conditions`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/nudges`, { headers: getAuthHeader() }),
      ]);
      setConditions(c.data || []);
      setNudges(n.data || []);
    } catch (e) {
      toast.error('Failed to load protocols');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addCondition = async () => {
    if (!newName.trim()) { toast.error('Enter a condition name'); return; }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/conditions`, {
        name: newName.trim(),
        keywords: newKeywords.split(',').map(k => k.trim()).filter(Boolean),
        pinned: [],
      }, { headers: getAuthHeader() });
      toast.success(`'${newName.trim()}' added`);
      setNewName('');
      setNewKeywords('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const addNudge = async () => {
    try {
      const res = await axios.post(`${API_URL}/nudges`, { match_text: 'product words', message: 'Your staff tip here...' }, { headers: getAuthHeader() });
      toast.success('New nudge added — edit it below');
      load();
    } catch (e) {
      toast.error('Add failed');
    }
  };

  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
  // Staff notes: emoji/short/colon lines become headings, quoted or ? lines are questions, arrow / Yes / No lines are follow-ups
  const renderNotes = (notes) => notes.split('\n').map(l => l.trim()).filter(Boolean).map(raw => {
    const line = raw.replace(/^[-•*]\s*/, '');
    const noEmoji = line.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '').trim();
    const isHeading = (noEmoji !== line) || /:$/.test(line) || (line.length <= 40 && !/[.?!"”]$/.test(line) && !/^(→|->|yes|no)\b/i.test(line));
    if (isHeading) return `<h3 class="note-h">${esc(noEmoji.replace(/:$/, ''))}</h3>`;
    if (/^["“]/.test(line) || /\?["”]?$/.test(line)) return `<p class="note-q">${esc(line.replace(/^["“]|["”]$/g, ''))}</p>`;
    if (/^(→|->|yes\b|no\b|\+)/i.test(line)) return `<p class="note-a">${esc(line.replace(/^(->)/, '→'))}</p>`;
    return `<p class="note-tip">${esc(line)}</p>`;
  }).join('');

  const pickRow = (name, benefit, caution, price, pinned, dose) => `
          <div class="pick">
            <div class="pick-left">
              <div class="pick-name">${pinned ? '<span class="staff-pick">★ STAFF PICK</span> ' : ''}${esc(name)}</div>
              ${benefit ? `<div class="pick-benefit">${esc(benefit)}</div>` : ''}
              ${caution ? `<div class="pick-caution">⚠ ${esc(caution)}</div>` : ''}
              ${dose ? `<div class="pick-dose">Dose: ${esc(dose)}</div>` : ''}
            </div>
            <div class="pick-price">${price == null ? '<span class="ask-staff">Ask staff</span>' : `$${(price || 0).toFixed(2)}`}</div>
          </div>`;
  const buildCardHTML = (card, storeName) => `
    <div class="card">
      <div class="card-head">
        <p class="eyebrow">Our Top Picks For</p>
        <h1>${esc(card.condition)}</h1>
      </div>
      <div class="picks">
        ${(card.pinned || []).map(p => pickRow(p.name, p.benefit, p.caution, p.sell_price, true, p.dose)).join('')}
        ${(card.stack || []).map(s => (s.products || []).map(p => pickRow(p.name, s.benefit, s.caution, p.sell_price, p.pinned, s.dose)).join('')).join('')}
      </div>
      ${(card.stack || []).some(s => s.dose) ? `
      <div class="dosing">
        <p class="dosing-title">Dosing Guide</p>
        <table>${(card.stack || []).filter(s => s.dose).map(s => `<tr><td class="dose-term">${esc(s.term)}</td><td>${esc(s.dose)}</td></tr>`).join('')}</table>
      </div>` : ''}
      ${(card.staff_notes || '').trim() ? `
      <div class="screening">
        <p class="screening-title">Staff Screening &amp; Tips</p>
        <p class="screening-sub">Ask these before recommending anything from this card</p>
        ${renderNotes(card.staff_notes)}
      </div>` : ''}
      <div class="card-foot">
        <p class="store">${esc(storeName)}</p>
        <p class="ask">Ask our team for personalised advice</p>
        <p class="disclaimer">Always read the label and use as directed. Supplements should not replace a balanced diet.</p>
      </div>
    </div>`;

  const printShelfCards = async (names) => {
    setPrinting(true);
    try {
      const bizName = localStorage.getItem('business_name') || 'SupplyTrack';
      const storeName = getCurrentStore()?.name ? `${bizName} — ${getCurrentStore().name}` : bizName;
      const cards = [];
      for (const name of names) {
        try {
          const res = await axios.get(
            `${API_URL}/conditions/shelf-card?condition=${encodeURIComponent(name)}&store=${selectedStore}`,
            { headers: getAuthHeader() }
          );
          if ((res.data.stack || []).some(s => (s.products || []).length) || (res.data.pinned || []).length > 0) cards.push(res.data);
        } catch (_) { /* skip conditions with no protocol */ }
      }
      if (!cards.length) { toast.error('Nothing to print — pin the products you want on the card first'); return; }
      const html = `<!DOCTYPE html><html><head><title>Protocol Shelf Cards</title><style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; color: #1e293b; }
        .card { width: 148mm; min-height: 100mm; border: 2.5px solid #0f766e; border-radius: 8px; padding: 9mm 10mm; margin: 6mm auto; page-break-inside: avoid; display: flex; flex-direction: column; }
        .card-head { text-align: center; border-bottom: 1.5px solid #0f766e; padding-bottom: 4mm; margin-bottom: 4mm; }
        .eyebrow { font-family: Arial, sans-serif; font-size: 9pt; letter-spacing: 2.5px; text-transform: uppercase; color: #0f766e; margin-bottom: 1.5mm; }
        h1 { font-size: 22pt; color: #134e4a; }
        .picks { flex: 1; }
        .pick { display: flex; justify-content: space-between; align-items: baseline; gap: 6mm; padding: 2.2mm 0; border-bottom: 1px dotted #cbd5e1; }
        .pick-left { flex: 1; }
        .pick-name { font-size: 11.5pt; }
        .pick-benefit { font-size: 9pt; font-style: italic; color: #475569; margin-top: 0.5mm; }
        .pick-caution { font-family: Arial, sans-serif; font-size: 7.5pt; color: #b45309; margin-top: 0.5mm; }
        .ask-staff { font-family: Arial, sans-serif; font-size: 7.5pt; font-weight: normal; color: #94a3b8; font-style: italic; }
        .screening { margin-top: 5mm; padding: 4mm 5mm; background: #fffbeb; border: 1.5px solid #f59e0b; border-radius: 4px; page-break-inside: auto; }
        .screening-title { font-family: Arial, sans-serif; font-size: 12pt; font-weight: bold; letter-spacing: 2px; text-transform: uppercase; color: #92400e; }
        .screening-sub { font-style: italic; font-size: 9.5pt; color: #b45309; margin-bottom: 3mm; padding-bottom: 2mm; border-bottom: 1px solid #fcd34d; }
        .note-h { font-family: Arial, sans-serif; font-size: 10.5pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #92400e; margin: 3.5mm 0 1.5mm; }
        .note-h:first-of-type { margin-top: 0; }
        .note-q { font-size: 11pt; color: #1e293b; margin: 1.2mm 0; padding-left: 4mm; position: relative; }
        .note-q::before { content: "?"; position: absolute; left: 0; color: #0f766e; font-weight: bold; font-family: Arial, sans-serif; }
        .note-a { font-family: Arial, sans-serif; font-size: 9.5pt; color: #78350f; margin: 0.8mm 0 1.2mm 8mm; }
        .note-tip { font-family: Arial, sans-serif; font-size: 9.5pt; color: #1e293b; margin: 1mm 0; padding-left: 4mm; position: relative; }
        .note-tip::before { content: "•"; position: absolute; left: 0.5mm; color: #b45309; }
        .pick-dose { font-family: Arial, sans-serif; font-size: 8pt; color: #0f766e; margin-top: 0.6mm; }
        .dosing { margin-top: 4mm; padding: 3mm 4mm; border: 1.5px solid #0f766e; border-radius: 4px; }
        .dosing-title { font-family: Arial, sans-serif; font-size: 10pt; font-weight: bold; letter-spacing: 2px; text-transform: uppercase; color: #134e4a; margin-bottom: 2mm; }
        .dosing table { width: 100%; border-collapse: collapse; }
        .dosing td { font-family: Arial, sans-serif; font-size: 8.5pt; color: #1e293b; padding: 1.2mm 1mm; border-bottom: 1px dotted #cbd5e1; vertical-align: top; }
        .dosing tr:last-child td { border-bottom: 0; }
        .dose-term { font-weight: bold; color: #0f766e; white-space: nowrap; width: 38%; }
        .staff-pick { font-family: Arial, sans-serif; font-size: 7pt; font-weight: bold; color: #b45309; letter-spacing: 1px; }
        .pick-price { font-family: Arial, sans-serif; font-size: 11.5pt; font-weight: bold; color: #0f766e; white-space: nowrap; }
        .card-foot { text-align: center; margin-top: 4mm; }
        .store { font-family: Arial, sans-serif; font-size: 9pt; font-weight: bold; letter-spacing: 1.5px; text-transform: uppercase; color: #134e4a; }
        .ask { font-style: italic; font-size: 9.5pt; margin-top: 1mm; }
        .disclaimer { font-family: Arial, sans-serif; font-size: 6.5pt; color: #94a3b8; margin-top: 2mm; }
        @media print { .card { margin: 0 auto 6mm; } }
      </style></head><body>${cards.map(c => buildCardHTML(c, storeName)).join('')}
      <script>window.onload = function() { window.print(); };</script></body></html>`;
      const w = window.open('', '_blank');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups to print shelf cards'); return; }
      w.document.write(html);
      w.document.close();
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;
  }

  return (
    <div className="space-y-8 p-6 max-w-5xl" data-testid="protocol-editor-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HeartPulse className="w-6 h-6 text-teal-600" /> Protocol Editor
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Edit the condition protocols, pinned products and scan nudges your staff see at the POS. Changes apply instantly.
        </p>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold uppercase tracking-wide text-slate-700">Conditions ({conditions.length})</h2>
          <Button size="sm" onClick={() => printShelfCards(conditions.map(c => c.name))} disabled={printing} variant="outline" className="border-teal-200 text-teal-700" data-testid="print-all-cards-btn">
            {printing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Printer className="w-4 h-4 mr-1" />} Print All Shelf Cards
          </Button>
        </div>
        <Card className="border-dashed border-2">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 w-full">
              <Label className="text-xs uppercase text-slate-500">New condition name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Migraine" data-testid="new-condition-name" />
            </div>
            <div className="flex-[2] w-full">
              <Label className="text-xs uppercase text-slate-500">Keywords (comma-separated)</Label>
              <Input value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)} placeholder="e.g. magnesium, riboflavin, feverfew, coq10" data-testid="new-condition-keywords" />
            </div>
            <Button onClick={addCondition} disabled={adding} className="bg-teal-600 hover:bg-teal-700 shrink-0" data-testid="add-condition-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Add Condition
            </Button>
          </CardContent>
        </Card>
        <div className="grid gap-3">
          {conditions.map(c => (
            <ConditionCard key={c.id || c.name} cond={c} onSaved={load} onDeleted={load} onPrint={(n) => printShelfCards([n])} getAuthHeader={getAuthHeader} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" /> Scan Nudges ({nudges.length})
          </h2>
          <Button size="sm" onClick={addNudge} variant="outline" className="border-teal-200 text-teal-700" data-testid="add-nudge-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Nudge
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          When a scanned product's name contains the match words (use | for alternatives, e.g. "turmeric|curcumin"), staff see the tip as a toast.
        </p>
        <div className="space-y-2">
          {nudges.map(n => (
            <NudgeRow key={n.id} nudge={n} onChanged={load} getAuthHeader={getAuthHeader} />
          ))}
        </div>
      </section>
    </div>
  );
}
